import {
    DirectLink,
    Email,
    EmailReferenceType,
    EmailParticipant,
    EmailTrainer,
    EmailType,
    Participant,
    ParticipantStatus,
    ProfileRecipient,
    Project,
    Recipients,
    Trainer,
} from "../types/PersologOnlineTypes.ts";
import { EmailServiceAPI } from "../api";
import { getProductNames } from "./ProjectUtils.ts";
import { i18n } from "../main.ts";

const baseUrl = import.meta.env.VITE_APP_PERSOLOG_URL;
const questionnairePath = import.meta.env.VITE_APP_QUESTIONNAIRE_FRONTEND_PATH;

function buildEmail(
    trainer: Trainer,
    participant: Participant,
    project: Project,
    product: string,
    type: EmailType,
    link: string,
): Email {
    const emailTrainer: EmailTrainer = {
        firstname: trainer.firstname,
        lastname: trainer.lastname,
        email: trainer.email,
        company: trainer.company,
    };
    const emailParticipant: EmailParticipant = {
        firstname: participant?.firstName,
        lastname: participant?.name,
        email: participant?.email,
    };
    const recipient = (() => {
        switch (project.recipients) {
            case Recipients.PARTICIPANT:
                return ProfileRecipient.PARTICIPANT;
            case Recipients.TRAINER:
                return ProfileRecipient.TRAINER;
            case Recipients.BOTH:
                return ProfileRecipient.BOTH;
            default:
                return ProfileRecipient.TRAINER;
        }
    })();

    return {
        type,
        profile: product,
        link,
        trainer: emailTrainer,
        participant: emailParticipant,
        profileRecipient: recipient,
        // Both emails built here go to the participant, so that is what they are referenced to
        // (RDT-365). Left off when the participant has no id yet, which only happens before it has
        // been saved; the delivery status then falls back to searching by address.
        ...(participant?.id === undefined || participant?.id === null
            ? {}
            : { reference: { type: EmailReferenceType.PARTICIPANT, id: String(participant.id) } }),
    };
}

export async function sendQuestionnaireEmail(trainer: Trainer, participant: Participant, project: Project) {
    const productNames = await getProductNames(project.language);
    const email = buildEmail(
        trainer,
        participant,
        project,
        productNames[participant.selectedProduct],
        EmailType.QUESTIONNAIRE,
        `${baseUrl}${questionnairePath}/${participant.questionnaire}?lang=${project.language}`,
    );
    await EmailServiceAPI.sendEmail(email, project.language);
}

/**
 * Sends the trainer a sample of the questionnaire email this project's participants receive
 * (RDT-542). The trainer stands in for the participant, so the email goes to their own address and
 * greets them by name; product, language and profile recipient come from the project. The button
 * in the email leads to a placeholder, not to a questionnaire: no participant is created and no
 * profile is used. Failures propagate so the caller can tell the trainer.
 */
export async function sendQuestionnaireEmailPreview(trainer: Trainer, project: Project) {
    const product = project.defaultProduct || project.product;
    const productNames = await getProductNames(project.language);
    const sampleParticipant = {
        firstName: trainer.firstname,
        name: trainer.lastname,
        email: trainer.email,
    } as Participant;
    const email: Email = {
        ...buildEmail(
            trainer,
            sampleParticipant,
            project,
            productNames[product] ?? product,
            EmailType.QUESTIONNAIRE,
            `${baseUrl}${questionnairePath}/preview?lang=${project.language}`,
        ),
        // The email goes to the trainer, so it is referenced to them (RDT-365) and never turns up
        // in a participant's delivery status.
        reference: { type: EmailReferenceType.TRAINER, id: trainer.id },
    };
    await EmailServiceAPI.sendEmailReportingErrors(email, project.language);
}

export async function sendProfileEmail(trainer: Trainer, participant: Participant, project: Project) {
    const productNames = await getProductNames(project.language);
    const link = `${baseUrl}/profile-download/profile/${participant.id}?lang=${project.language}`;
    const email = buildEmail(
        trainer,
        participant,
        project,
        productNames[participant.selectedProduct],
        EmailType.PARTICIPANT_PORTAL,
        link,
    );
    await EmailServiceAPI.sendEmail(email, project.language);
}

/**
 * Send an email to the participant with the given subject
 * @param subject
 * @param sender
 * @param recipient
 */
export function sendEmail(subject: string, sender: Trainer, recipient: Participant) {
    const encodedSubject = encodeURIComponent(subject);
    const encodedMessage = encodeURIComponent(`${recipient?.firstName},\n\n\n\n${sender?.firstname}`);
    globalThis.location.href = `mailto:${recipient?.email}?subject=${encodedSubject}&body=${encodedMessage}`;
}

/**
 * Send an email to the participant with the given subject and message
 * @param subject
 * @param recipient
 * @param message
 */
export function sendEmailWithMessage(subject: string, recipient: Participant, message: string) {
    const encodedSubject = encodeURIComponent(subject);
    const encodedMessage = encodeURIComponent(`${message}`);
    globalThis.location.href = `mailto:${recipient?.email}?subject=${encodedSubject}&body=${encodedMessage}`;
}

export type ParticipantLinkKind = "questionnaire" | "download";

export type ParticipantLink = {
    url: string;
    kind: ParticipantLinkKind;
};

/**
 * Resolves the link to embed in a personal email for a participant, following the
 * approved status mapping:
 *  - ACTIVATED / STARTED → personal questionnaire link, or the project's direct
 *                          link for direct-link projects (short URL preferred)
 *  - NOTIFIED            → profile download link
 *  - anything else       → no link (returns null)
 *
 * Returns null when the required data (questionnaire id, direct-link hash,
 * participant id) is missing, so callers can disable the action. COMPLETED and
 * GENERATED intentionally yield null: the questionnaire is done and the profile
 * has not been released yet (sending the download link would bypass the release).
 */
export function resolveParticipantLink(
    participant: Participant,
    project: Project,
    directLink?: DirectLink | null,
): ParticipantLink | null {
    const lang = project.language;
    switch (participant.status) {
        case ParticipantStatus.ACTIVATED:
        case ParticipantStatus.STARTED:
            if (directLink && (directLink.shortUrl || directLink.hash)) {
                const url =
                    directLink.shortUrl ||
                    `${baseUrl}${questionnairePath}/direct-link/${directLink.hash}?lang=${lang}`;
                return { url, kind: "questionnaire" };
            }
            if (participant.questionnaire) {
                return {
                    url: `${baseUrl}${questionnairePath}/${participant.questionnaire}?lang=${lang}`,
                    kind: "questionnaire",
                };
            }
            return null;
        case ParticipantStatus.NOTIFIED:
            if (participant.id !== undefined && participant.id !== null) {
                return {
                    url: `${baseUrl}/profile-download/profile/${participant.id}?lang=${lang}`,
                    kind: "download",
                };
            }
            return null;
        default:
            return null;
    }
}

/**
 * Opens the trainer's own mail client (mailto:) pre-filled with a short message —
 * rendered in the project's language — that embeds the participant's relevant
 * link (questionnaire/direct link, or the profile download once NOTIFIED).
 * Does nothing if no link is available for the participant's current status.
 */
export function sendEmailWithLink(
    trainer: Trainer,
    participant: Participant,
    project: Project,
    directLink?: DirectLink | null,
) {
    const link = resolveParticipantLink(participant, project, directLink);
    if (!link) {
        return;
    }
    const locale = project.language;
    const subject = i18n.global.t(
        `project-details.participant.email-link.subject-${link.kind}`,
        {},
        { locale },
    );
    const body = i18n.global.t(
        `project-details.participant.email-link.body-${link.kind}`,
        {
            firstName: participant.firstName,
            link: link.url,
            trainer: `${trainer.firstname} ${trainer.lastname}`,
        },
        { locale },
    );
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    globalThis.location.href = `mailto:${participant.email}?subject=${encodedSubject}&body=${encodedBody}`;
}

/**
 * Opens the trainer's mail client addressed to the trainer with every participant
 * in BCC and a blank body — the long-standing "contact everyone at once" action.
 */
export function sendBlankEmailToAll(trainer: Trainer, participants: Participant[], project: Project) {
    const bcc = participants.map((p) => p.email).join(";");
    const encodedSubject = encodeURIComponent(project.name);
    globalThis.location.href = `mailto:${trainer.email}?bcc=${bcc}&subject=${encodedSubject}`;
}

/**
 * Participants who can still make use of the direct link — everyone up to and
 * including STARTED (i.e. not yet completed/generated/notified/revoked/deleted),
 * so re-sending the link only reaches people for whom it is still actionable.
 */
export function directLinkRecipients(participants: Participant[]): Participant[] {
    const eligible = new Set([ParticipantStatus.CREATED, ParticipantStatus.ACTIVATED, ParticipantStatus.STARTED]);
    return participants.filter((p) => p.status !== undefined && eligible.has(p.status));
}

/**
 * Opens the trainer's mail client with the project's direct link (short URL
 * preferred when present) addressed to the trainer, with every still-eligible
 * participant (status up to STARTED) in BCC. Body is rendered in the project
 * language. Direct-link projects only — no-op when there is no direct link or no
 * eligible recipient.
 */
export function sendDirectLinkToAll(
    trainer: Trainer,
    participants: Participant[],
    project: Project,
    directLink?: DirectLink | null,
) {
    if (!directLink || (!directLink.shortUrl && !directLink.hash)) {
        return;
    }
    const recipients = directLinkRecipients(participants);
    if (recipients.length === 0) {
        return;
    }
    const link =
        directLink.shortUrl ||
        `${baseUrl}${questionnairePath}/direct-link/${directLink.hash}?lang=${project.language}`;
    const bcc = recipients.map((p) => p.email).join(";");
    const locale = project.language;
    const subject = i18n.global.t(
        "project-details.participants.email-all.direct-link-subject",
        {},
        { locale },
    );
    const body = i18n.global.t(
        "project-details.participants.email-all.direct-link-body",
        { link, trainer: `${trainer.firstname} ${trainer.lastname}` },
        { locale },
    );
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    globalThis.location.href = `mailto:${trainer.email}?bcc=${bcc}&subject=${encodedSubject}&body=${encodedBody}`;
}
